import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import colour from 'client/utils/constants/GDS/js/color';

const defaultSize = 30;

const TriangleIcon = ({ fill, points, radius, radius1, radius2, angle, stroke, scale, ...props }) => {
    const canvasRef = useRef();
    const [size, setSize] = useState(defaultSize);

    useEffect(() => {
        updateSize();
        updateCanvas();
    }, [fill, points, radius, radius1, radius2, angle, stroke, scale]);

    const updateSize = () => {
        const maxWidth = Math.sqrt(Math.pow(radius * 2, 2) + Math.pow(radius * 2, 2));
        if (maxWidth >= size) {
            setSize(maxWidth);
        } else {
            setSize(defaultSize);
        }
    };

    const updateCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Reset canvas when updating, otherwise previous drawings remain
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.beginPath();
        ctx.moveTo(0.20 * size, 0.80 * size);
        ctx.lineTo(0.80 * size, 0.80 * size);
        ctx.lineTo(0.50 * size, 0.20 * size);

        if (fill?.color) {
            ctx.fillStyle = fill?.color;
            ctx.fill();
        }

        ctx.strokeStyle = stroke?.color;
        ctx.lineCap = stroke?.lineCap;
        ctx.lineJoin = stroke?.lineJoin;
        if (stroke?.lineDash) {
            ctx.setLineDash(stroke?.lineDash);
        }
        ctx.setMiterLimit = stroke?.miterLimit;
        ctx.lineDashOffset = stroke?.lineDashOffset;
        ctx.lineWidth = stroke?.width;
        ctx.scale(scale, scale);
    };

    return (
        <canvas
            ref={canvasRef}
            width={size}
            height={size}
            data-testid={props['data-testid']}
        />
    );
};

TriangleIcon.propTypes = {
    fill: PropTypes.shape({
        color: PropTypes.string
    }),
    points: PropTypes.number,
    radius: PropTypes.number,
    radius1: PropTypes.number,
    radius2: PropTypes.number,
    angle: PropTypes.number,
    stroke: PropTypes.shape({
        color: PropTypes.string,
        lineCap: PropTypes.oneOf(['butt', 'round', 'square']),
        lineJoin: PropTypes.oneOf(['bevel', 'round', 'miter']),
        lineDash: PropTypes.arrayOf(PropTypes.number),
        lineDashOffset: PropTypes.number,
        miterLimit: PropTypes.number,
        width: PropTypes.number
    }),
    scale: PropTypes.number,
    'data-testid': PropTypes.string
};

TriangleIcon.defaultProps = {
    fill: {
        color: colour.black
    },
    points: 3,
    radius: 10,
    angle: 0,
    stroke: {
        color: colour.black,
        lineCap: 'round',
        lineJoin: 'round',
        lineDashOffset: 0,
        miterLimit: 10,
        width: 3
    },
    displacement: [0, 0],
    rotation: 0
};

export default TriangleIcon;
